import { IGroupedScopeClause } from "@/pages/frontend/scope/types";

interface IHighlightAreaWithContext {
    left: number;
    top: number;
    width: number;
    height: number;
    page_index: number;
    scope_clause_id: number;
    clause_id: number;
}

export interface IHighlightOverlap {
    area1: IHighlightAreaWithContext;
    area2: IHighlightAreaWithContext;
}

/**
 * Checks if two rectangular highlight areas overlap on the same page
 *
 * @param area1 - First highlight area
 * @param area2 - Second highlight area
 * @returns True if the areas overlap, false otherwise
 */
const doHighlightAreasOverlap = (
    area1: IHighlightAreaWithContext,
    area2: IHighlightAreaWithContext
): boolean => {
    // Areas must be on the same page to overlap
    if (area1.page_index !== area2.page_index) {
        return false;
    }

    // Areas must belong to different scope clauses
    if (area1.scope_clause_id === area2.scope_clause_id) {
        return false;
    }

    // Two rectangles overlap if they intersect in both x and y dimensions.
    const xOverlap =
        area1.left < area2.left + area2.width &&
        area2.left < area1.left + area1.width;

    const yOverlap =
        area1.top < area2.top + area2.height &&
        area2.top < area1.top + area1.height;

    return xOverlap && yOverlap;
};

/**
 * Finds all overlapping highlight areas between different scope clauses
 *
 * @param groupedScopeClauses - Array of grouped scope clauses to check for overlaps
 * @param pageIndex - Optional page index to limit search to specific page
 * @returns Array of overlap objects describing all detected overlaps
 */
export const findHighlightAreaOverlaps = (
    groupedScopeClauses: IGroupedScopeClause[],
    pageIndex?: number
): IHighlightOverlap[] => {
    const overlaps: IHighlightOverlap[] = [];

    // Flatten all highlight areas with their context
    const allHighlightAreas: IHighlightAreaWithContext[] = [];

    groupedScopeClauses.forEach((group) => {
        group.scopeClauses.forEach((scopeClause) => {
            scopeClause.highlight_areas
                .filter(
                    (area) =>
                        pageIndex === undefined || area.page_index === pageIndex
                )
                .forEach((area) => {
                    allHighlightAreas.push({
                        ...area,
                        clause_id: scopeClause.clause.id,
                        scope_clause_id: scopeClause.id,
                    });
                });
        });
    });

    // Check all pairs of highlight areas for overlaps
    for (let i = 0; i < allHighlightAreas.length; i++) {
        for (let j = i + 1; j < allHighlightAreas.length; j++) {
            const area1 = allHighlightAreas[i];
            const area2 = allHighlightAreas[j];

            if (doHighlightAreasOverlap(area1, area2)) {
                overlaps.push({
                    area1,
                    area2,
                });
            }
        }
    }

    return overlaps;
};

export const getScopeClauseCountWithOverlaps = (
    overlaps: IHighlightOverlap[]
) => {
    const scopeClauseIds = new Set<number>();

    overlaps.forEach((overlap) => {
        scopeClauseIds.add(overlap.area1.scope_clause_id);
        scopeClauseIds.add(overlap.area2.scope_clause_id);
    });

    return scopeClauseIds.size;
};
